import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save, Trophy, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserMention } from '@/components/UserMention';
import { useGuildRights } from '@/lib/access';

interface Entry {
  rank: number;
  userId: string;
  xp: number;
  level: number;
  warnings: number;
}

export function Leaderboard() {
  const { guildId } = useParams<{ guildId: string }>();
  const canEdit = useGuildRights(guildId).can('leveling', 'edit');

  const { data, isLoading, error } = useQuery({
    queryKey: ['leaderboard', guildId],
    queryFn: () =>
      api<{ entries: Entry[] }>(`/api/guilds/${guildId}/leaderboard?limit=100`),
    enabled: !!guildId,
    refetchInterval: 60_000,
  });

  const entries = data?.entries ?? [];

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="mb-6 text-2xl font-semibold">XP-Rangliste</h1>

      {isLoading && <p className="text-muted-foreground">Lade…</p>}
      {error && <p className="text-destructive">Fehler: {(error as Error).message}</p>}

      {!isLoading && entries.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Noch keine XP-Daten</CardTitle>
            <CardDescription>
              Sobald Mitglieder Nachrichten schreiben, sammeln sie XP und tauchen hier auf.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {entries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y">
              {entries.map((e) => (
                <LeaderboardRow
                  key={e.userId}
                  entry={e}
                  guildId={guildId!}
                  canEdit={canEdit}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function LeaderboardRow({
  entry,
  guildId,
  canEdit,
}: {
  entry: Entry;
  guildId: string;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [level, setLevel] = useState(entry.level);
  const [xp, setXp] = useState(entry.xp);
  const qc = useQueryClient();

  const saveMut = useMutation({
    mutationFn: () =>
      api(`/api/guilds/${guildId}/leaderboard/${entry.userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ level, xp }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['leaderboard', guildId] });
      setEditing(false);
    },
  });

  return (
    <li className="p-3">
      <div className="flex items-center gap-4">
        <div className="flex w-10 shrink-0 items-center justify-center">
          <RankBadge rank={entry.rank} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">
            <UserMention id={entry.userId} />
          </div>
          <div className="text-xs text-muted-foreground">
            {entry.warnings > 0 && (
              <span className="text-yellow-500">⚠ {entry.warnings} Warnings · </span>
            )}
            Level {entry.level}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-sm">
            {entry.xp.toLocaleString('de-DE')} XP
          </div>
          <div className="text-xs text-muted-foreground">Level {entry.level}</div>
        </div>
        {canEdit && !editing && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setLevel(entry.level);
              setXp(entry.xp);
              setEditing(true);
            }}
            title="Bearbeiten"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
      </div>

      {editing && (
        <div className="mt-3 flex flex-wrap items-end gap-2 rounded-md border bg-muted/40 p-3">
          <div className="w-24">
            <label className="text-xs font-medium">Level</label>
            <Input
              type="number"
              min={0}
              max={1000}
              value={level}
              onChange={(e) => setLevel(Math.max(0, Math.min(1000, Number(e.target.value) || 0)))}
            />
          </div>
          <div className="w-32">
            <label className="text-xs font-medium">XP</label>
            <Input
              type="number"
              min={0}
              value={xp}
              onChange={(e) => setXp(Math.max(0, Number(e.target.value) || 0))}
            />
          </div>
          <div className="flex flex-1 justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              <X className="mr-1 h-3.5 w-3.5" /> Abbrechen
            </Button>
            <Button
              size="sm"
              disabled={saveMut.isPending || (level === entry.level && xp === entry.xp)}
              onClick={() => saveMut.mutate()}
            >
              <Save className="mr-1 h-3.5 w-3.5" />
              {saveMut.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
          {saveMut.error && (
            <p className="w-full text-xs text-destructive">
              {(saveMut.error as Error).message}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <Trophy className="h-6 w-6 text-yellow-400" />;
  if (rank === 2) return <Trophy className="h-6 w-6 text-gray-300" />;
  if (rank === 3) return <Trophy className="h-6 w-6 text-amber-700" />;
  return <span className="font-mono text-sm text-muted-foreground">#{rank}</span>;
}
